import React from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ProjectIcon from '@atlaskit/icon/core/project';

import { ProductKeys, useProductConfig } from '../../../../../common/constants/products';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { FilterButtonIconContainer } from '../../../../../common/ui/quick-find/filter-button/filter-button-icon-container';
import { FilterPopupSelect } from '../../../../../common/ui/quick-find/filter-popup-select';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store';
import { filter as projectFilter } from '../../../../../controllers/store/search/filters/project';

import { i18n } from './messages';

export const ProjectFilter = () => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: projectFilter.id,
	});

	const JiraIcon = useProductConfig(ProductKeys.Jira).Icon;

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
		firstSelectedOptionLabel,
		badgeLabel,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.jiraFilterProjectLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.jiraFilterProjectLabelUnselected),
			options: suggestedOptions,
		},
	];

	const getIconBefore = () => {
		return (
			<FilterButtonIconContainer>
				<JiraIcon size="xxsmall" />
				<ProjectIcon
					label={formatMessage(
						FeatureGates.getExperimentValue('project-terminology-refresh', 'isEnabled', false)
							? i18n.projectGalaxia
							: i18n.project,
					)}
				/>
			</FilterButtonIconContainer>
		);
	};

	const getIconAfter = () => {
		return selectedOptions.length === 0 ? (
			<ChevronDownIcon label="" size="small" />
		) : (
			badgeLabel && <Badge appearance="primary">{badgeLabel}</Badge>
		);
	};

	return (
		<FilterPopupSelect
			testId="search-dialog-project-filter"
			filterId={projectFilter.id}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={setOption}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={
				firstSelectedOptionLabel
					? firstSelectedOptionLabel
					: formatMessage(
							FeatureGates.getExperimentValue('project-terminology-refresh', 'isEnabled', false)
								? i18n.projectGalaxia
								: i18n.project,
						)
			}
			placeholder={formatMessage(
				FeatureGates.getExperimentValue('project-terminology-refresh', 'isEnabled', false)
					? i18n.jiraFilterProjectPlaceholderGalaxia
					: i18n.jiraFilterProjectPlaceholder,
			)}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={clearOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			target={({ isOpen, ...targetProps }) => (
				<FilterButton
					testId="search-dialog-project-filter-button"
					{...targetProps}
					isSelected={!!selectedOptions.length || isOpen}
					iconBefore={getIconBefore()}
					iconAfter={getIconAfter()}
				>
					{firstSelectedOptionLabel
						? firstSelectedOptionLabel
						: formatMessage(
								FeatureGates.getExperimentValue('project-terminology-refresh', 'isEnabled', false)
									? i18n.projectGalaxia
									: i18n.project,
							)}
				</FilterButton>
			)}
		/>
	);
};
